import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCalendarAlt, 
  FaUsers, 
  FaLaptop, 
  FaMobileAlt, 
  FaShieldAlt, 
  FaRocket, 
  FaCheck, 
  FaPhone, 
  FaEnvelope,
  FaInstagram,
  FaFacebookF,
  FaTwitter,
  FaMapMarkerAlt,
  FaClipboardCheck,
  FaCrown,
  FaGlassCheers
} from 'react-icons/fa';
import { BsGraphUp, BsCreditCard2Front, BsLightningCharge } from 'react-icons/bs';
import { MdEventAvailable, MdEventSeat, MdCelebration, MdOutlineDesignServices } from 'react-icons/md';
import { RiCustomerServiceFill } from 'react-icons/ri';
import { GiPartyPopper, GiMicrophone } from 'react-icons/gi';

const EventManagementSiteLanding = () => {
  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    eventType: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    submitting: false,
    error: false,
    message: ''
  });

  // Modal state
  const [showDemoModal, setShowDemoModal] = useState(false);

  // Show demo modal after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDemoModal(true);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ 
      submitted: false, 
      submitting: true,
      error: false,
      message: ''
    });
    
    // Simulate form submission
    setTimeout(() => {
      setFormStatus({ 
        submitted: true, 
        submitting: false,
        error: false,
        message: 'Thank you! Our team will contact you shortly to discuss your event management website needs.'
      });
      // Reset form
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        eventType: '',
        message: '',
      });
    }, 1500);
  };

  // Event types options
  const eventTypes = [
    { value: '', label: 'Select Event Type' },
    { value: 'corporate', label: 'Corporate Events' },
    { value: 'wedding', label: 'Weddings & Celebrations' },
    { value: 'conference', label: 'Conferences & Seminars' },
    { value: 'exhibition', label: 'Exhibitions & Trade Shows' },
    { value: 'social', label: 'Social Gatherings' },
    { value: 'concert', label: 'Concerts & Performances' },
    { value: 'festival', label: 'Festivals & Public Events' },
    { value: 'multi', label: 'Multiple Event Types' }
  ];

  // Feature list
  const features = [
    { 
      icon: <MdEventAvailable />, 
      title: "Event Booking System", 
      description: "A seamless booking platform allowing clients to view availability, packages, and secure their date instantly."
    },
    { 
      icon: <FaUsers />, 
      title: "RSVP Management", 
      description: "Digital invitation delivery, RSVP tracking, and guest management tools with custom forms for dietary preferences."
    },
    { 
      icon: <MdEventSeat />, 
      title: "Seating Planner", 
      description: "Interactive seating arrangement tools that help clients visualize and perfect their event layout."
    },
    { 
      icon: <FaClipboardCheck />, 
      title: "Vendor Dashboard", 
      description: "Manage all your vendors, contracts, and communications in one centralized location."
    },
    { 
      icon: <MdCelebration />, 
      title: "Event Portfolio", 
      description: "Stunning galleries showcasing your previous events, sorted by type, venue, and style."
    },
    { 
      icon: <FaMobileAlt />, 
      title: "Mobile Optimization", 
      description: "A responsive website that works flawlessly on all devices, allowing clients to plan on the go."
    }
  ];

  // Testimonials
  const testimonials = [
    {
      name: "Priya Mehta",
      position: "Corporate Event Manager",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=150&h=150",
      quote: "Our event management company's website has transformed how we do business. The booking system has automated 90% of our administrative work, and our client inquiries have tripled!"
    },
    {
      name: "James Wilson",
      position: "Wedding Planner",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=150&h=150",
      quote: "The virtual venue tours and seating planner features have revolutionized how we help couples plan their big day. Our clients are amazed by the technology."
    },
    {
      name: "Neha Sharma",
      position: "Festival Organizer",
      image: "https://images.unsplash.com/photo-1558203728-00f45181dd84?auto=format&fit=crop&w=150&h=150",
      quote: "The ticketing integration and event schedule features have made managing large-scale festivals much more streamlined. Plus, our website looks absolutely stunning!"
    }
  ];

  // Portfolio examples
  const portfolioExamples = [
    {
      title: "Corporate Excellence",
      image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=600&h=400",
      type: "Corporate Event"
    },
    {
      title: "Dream Wedding",
      image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=600&h=400",
      type: "Wedding Planning"
    },
    {
      title: "Tech Conference",
      image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=600&h=400",
      type: "Conference Management"
    },
    {
      title: "Summer Festival",
      image: "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?auto=format&fit=crop&w=600&h=400",
      type: "Festival Organization"
    }
  ];

  // Plans/pricing
  const plans = [
    {
      name: "Starter",
      price: "₹39,999",
      description: "Perfect for new event management companies",
      features: [
        "Responsive website design",
        "Event portfolio showcase",
        "Basic inquiry form",
        "Social media integration",
        "Mobile-friendly design",
        "3 months free maintenance"
      ],
      cta: "Get Started"
    },
    {
      name: "Professional",
      price: "₹79,999",
      description: "For established event management businesses",
      features: [
        "Everything in Starter",
        "Online booking system",
        "Client dashboard",
        "Payment integration",
        "Event photo galleries",
        "Vendor management",
        "6 months free maintenance"
      ],
      cta: "Most Popular",
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "₹129,999",
      description: "Complete solution for large event companies",
      features: [
        "Everything in Professional",
        "Virtual venue tours",
        "Interactive seating planner",
        "Custom event apps",
        "Ticketing integration",
        "Analytics dashboard",
        "1 year free maintenance"
      ],
      cta: "Contact Us"
    }
  ];

  return (
    <div className="bg-cyber-dark overflow-hidden">
      {/* Hero Section */}
      <section className="min-h-screen relative flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="w-full h-full bg-[linear-gradient(90deg,rgba(157,78,221,0.03)_1px,transparent_1px),linear-gradient(0deg,rgba(157,78,221,0.03)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
          <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-cyber-dark to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-cyber-dark to-transparent"></div>
          
          {/* Animated particles */}
          {[...Array(20)].map((_, i) => (
            <div 
              key={i} 
              className="absolute bg-cyber-pink rounded-full opacity-20 animate-float"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`
              }}
            ></div>
          ))}
        </div>

        <div className="container mx-auto px-6 pt-32 pb-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl font-orbitron font-bold mb-6">
                <span className="text-white">Expert </span>
                <span className="text-cyber-pink">Event Management</span>
                <span className="text-white"> Websites</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Elevate your event planning business with a cutting-edge website designed 
                specifically for event managers. Streamline bookings, showcase your portfolio, 
                and grow your client base with powerful digital tools.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => setShowDemoModal(true)}
                  className="cyber-button"
                >
                  <span>Request Free Demo</span>
                </button>
                <a 
                  href="#plans" 
                  className="cyber-button-outline"
                >
                  <span>View Pricing</span>
                </a>
              </div>
              
              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <span className="px-3 py-1 bg-cyber-pink/20 border border-cyber-pink rounded-full text-sm text-cyber-pink font-medium">
                  Limited Time Offer
                </span>
                <p className="text-cyber-neon text-sm">Get 20% off any package when you sign up before July 31!</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="cyber-card p-1 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1505236858219-8359eb29e329?auto=format&fit=crop&w=1000&q=80" 
                  alt="Event management dashboard" 
                  className="w-full h-auto rounded"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-cyber-dark/80 via-transparent to-transparent rounded"></div>
                
                {/* Floating badges */}
                <div className="absolute top-4 left-4 px-4 py-2 bg-cyber-dark/80 border border-cyber-pink rounded-full text-white flex items-center gap-2">
                  <FaCalendarAlt className="text-cyber-pink" />
                  <span>Booking System</span>
                </div>
                
                <div className="absolute bottom-4 right-4 px-4 py-2 bg-cyber-dark/80 border border-cyber-neon rounded-full text-white flex items-center gap-2">
                  <GiPartyPopper className="text-cyber-neon" />
                  <span>Event Portfolio</span>
                </div>
              </div>
              
              {/* Stats floating */}
              <div className="absolute -bottom-10 -left-10 cyber-card p-4 flex flex-col items-center justify-center w-32 h-32">
                <span className="text-3xl font-bold text-cyber-neon">300+</span>
                <span className="text-xs text-center text-gray-300">Events Managed</span>
              </div>
              
              <div className="absolute -top-5 -right-5 cyber-card p-4 flex flex-col items-center justify-center w-28 h-28">
                <span className="text-3xl font-bold text-cyber-pink">63%</span>
                <span className="text-xs text-center text-gray-300">Booking Increase</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action Banner */}
      <section className="py-8 bg-gradient-to-r from-cyber-pink/20 to-cyber-purple/20 border-y border-cyber-pink/30">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-orbitron font-bold text-white">Ready to transform your event management business?</h2>
              <p className="text-gray-300">Take the first step towards a powerful digital presence today.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="#plans" className="cyber-button">
                <span>View Our Solutions</span>
              </a>
              <button 
                onClick={() => setShowDemoModal(true)}
                className="cyber-button-outline"
              >
                <span>Free Consultation</span>
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              <span className="text-cyber-neon">Features</span> For Event Managers
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our websites are designed specifically for event management companies with tools that streamline your process and impress your clients.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="cyber-card p-6 hover:shadow-cyber-pink/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="bg-cyber-pink/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 text-cyber-pink text-2xl">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-orbitron font-bold mb-3 text-white">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <a href="#contact" className="cyber-button">
              <span>Get These Features</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Examples */}
      <section className="py-20 bg-cyber-gray/10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyber-pink/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyber-neon/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Our <span className="text-cyber-pink">Event</span> Showcases
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              See how we've helped event management companies present their services with stunning website designs.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {portfolioExamples.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden cyber-card p-1"
              >
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="w-full h-80 object-cover rounded transition-all duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyber-dark to-transparent opacity-80"></div>
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="text-xl font-orbitron font-bold text-white">{item.title}</h3>
                  <p className="text-cyber-pink">{item.type}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <button 
              onClick={() => setShowDemoModal(true)}
              className="cyber-button"
            >
              <span>Get Your Custom Design</span>
            </button>
          </motion.div>
        </div>
      </section>
      
      {/* Why Choose Us */}
      <section className="py-20 bg-cyber-dark relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Why <span className="text-cyber-pink">Choose</span> Us
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We understand the unique digital needs of event management professionals.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="bg-cyber-neon/20 p-2 rounded-lg mr-4">
                    <FaLaptop className="text-cyber-neon text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Event-Focused Design</h3>
                    <p className="text-gray-300">
                      Websites that showcase your events with interactive galleries, virtual tours, and booking systems.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-pink/20 p-2 rounded-lg mr-4">
                    <FaShieldAlt className="text-cyber-pink text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Secure Payment Integration</h3>
                    <p className="text-gray-300">
                      Built-in payment processing for deposits, ticket sales, and package bookings with full security.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-purple/20 p-2 rounded-lg mr-4">
                    <RiCustomerServiceFill className="text-cyber-purple text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Specialized Support</h3>
                    <p className="text-gray-300">
                      Our team understands event management and provides specialized support for your unique business needs.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-neon/20 p-2 rounded-lg mr-4">
                    <BsCreditCard2Front className="text-cyber-neon text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">ROI-Focused Packages</h3>
                    <p className="text-gray-300">
                      Flexible pricing designed to deliver immediate return on investment for event businesses of all sizes.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="cyber-card p-1">
                <img 
                  src="https://images.unsplash.com/photo-1531058020387-3be344556be6?auto=format&fit=crop&w=800&h=600&q=80" 
                  alt="Event planner using laptop" 
                  className="w-full h-auto rounded"
                />
              </div>
              
              {/* Stats overlay */}
              <div className="absolute -bottom-5 -right-5 bg-cyber-dark border border-cyber-neon p-6 rounded-lg shadow-lg">
                <div className="text-center">
                  <div className="flex justify-between gap-8">
                    <div>
                      <p className="text-3xl font-bold text-cyber-neon">97%</p>
                      <p className="text-sm text-gray-400">Client Satisfaction</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-cyber-pink">4x</p>
                      <p className="text-sm text-gray-400">Revenue Growth</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-cyber-gray/10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              What <span className="text-cyber-pink">Event Planners</span> Say
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Hear from event management professionals who have transformed their online presence with our websites.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="cyber-card p-6 hover:shadow-cyber-neon/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name} 
                    className="w-16 h-16 rounded-full border-2 border-cyber-pink mr-4"
                  />
                  <div>
                    <h4 className="text-lg font-orbitron font-bold text-white">{testimonial.name}</h4>
                    <p className="text-sm text-cyber-neon">{testimonial.position}</p>
                  </div>
                </div>
                <p className="text-gray-300">"{testimonial.quote}"</p>
                <div className="mt-4 flex">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-cyber-pink" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <button 
              onClick={() => setShowDemoModal(true)}
              className="cyber-button"
            >
              <span>Join These Event Planners</span>
            </button>
          </motion.div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section id="plans" className="py-20 bg-cyber-dark relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyber-pink/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyber-neon/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              <span className="text-cyber-neon">Pricing</span> Plans
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose the perfect package for your event management business.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`cyber-card p-6 flex flex-col h-full relative ${plan.highlighted ? 'border-cyber-pink' : ''}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-cyber-pink rounded-full text-white text-sm font-medium">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-2xl font-orbitron font-bold mb-2 text-white">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-3xl font-bold text-cyber-neon">{plan.price}</span>
                  <span className="text-gray-400 ml-1">one-time</span>
                </div>
                <p className="text-gray-300 mb-6">{plan.description}</p>
                
                <div className="flex-grow mb-8">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <FaCheck className="text-cyber-pink mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button 
                  onClick={() => setShowDemoModal(true)} 
                  className={`${plan.highlighted ? 'cyber-button' : 'cyber-button-outline'} text-center`}
                >
                  <span>{plan.cta}</span>
                </button>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-gray-400 mb-4">Need a custom solution? Contact us for a tailored quote.</p>
            <button 
              onClick={() => setShowDemoModal(true)} 
              className="text-cyber-pink hover:underline"
            >
              Request Custom Package →
            </button>
          </motion.div>
        </div>
      </section>
      
      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-cyber-gray/10 relative">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Get <span className="text-cyber-pink">Started</span> Today
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Fill out the form below and our team will contact you within 24 hours to discuss your event management website needs.
            </p>
          </motion.div>
          
          <div className="max-w-4xl mx-auto">
            <div className="cyber-card p-8">
              {formStatus.submitted ? (
                <div className="text-center py-8">
                  <div className="bg-cyber-neon/20 border border-cyber-neon text-white p-6 rounded mb-6 inline-block">
                    <svg className="w-16 h-16 mx-auto text-cyber-neon mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <p className="text-xl">{formStatus.message}</p>
                  </div>
                  <button 
                    onClick={() => setFormStatus({submitted: false, submitting: false, error: false, message: ''})}
                    className="cyber-button"
                  >
                    <span>Submit Another Request</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="companyName" className="block text-white mb-2">Company Name <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="text" 
                        id="companyName"
                        name="companyName" 
                        value={formData.companyName}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Your event management company name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contactName" className="block text-white mb-2">Your Name <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="text" 
                        id="contactName"
                        name="contactName" 
                        value={formData.contactName}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="email" className="block text-white mb-2">Email Address <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="email" 
                        id="email"
                        name="email" 
                        value={formData.email}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your email"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-white mb-2">Phone Number <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="tel" 
                        id="phone"
                        name="phone" 
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="eventType" className="block text-white mb-2">Event Type <span className="text-cyber-pink">*</span></label>
                    <select
                      id="eventType"
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      required
                      className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                    >
                      {eventTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-8">
                    <label htmlFor="message" className="block text-white mb-2">Additional Information</label>
                    <textarea 
                      id="message"
                      name="message" 
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                      placeholder="Tell us about your requirements, timeline, and specific features you're interested in..."
                    ></textarea>
                  </div>
                  
                  {formStatus.error && (
                    <div className="bg-cyber-pink/20 border border-cyber-pink text-white p-4 rounded mb-6">
                      <p>{formStatus.message}</p>
                    </div>
                  )}
                  
                  <div className="flex justify-center">
                    <button 
                      type="submit" 
                      className="cyber-button w-full md:w-auto"
                      disabled={formStatus.submitting}
                    >
                      <span>{formStatus.submitting ? 'Sending...' : 'Get Free Consultation'}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-gray-300 mb-4">Prefer to speak with us directly?</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <a href="mailto:ankitmaharshi55@gmail.com" className="text-white hover:text-cyber-neon transition-colors flex items-center gap-2">
                  <FaEnvelope /> ankitmaharshi55@gmail.com
                </a>
                <a href="tel:+919468532536" className="text-white hover:text-cyber-neon transition-colors flex items-center gap-2">
                  <FaPhone /> +91-9468532536
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-cyber-dark border-t border-cyber-purple/30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold mb-6">
              Ready to <span className="text-cyber-pink">Transform</span> Your Event Business?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Take your event management company to the next level with a stunning website that showcases your services and streamlines your processes.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button 
                onClick={() => setShowDemoModal(true)}
                className="cyber-button"
              >
                <span>Get Started Now</span>
              </button>
              <button 
                onClick={() => setShowDemoModal(true)}
                className="cyber-button-outline"
              >
                <span>Schedule Demo</span>
              </button>
            </div>

            <div className="mt-8 bg-gradient-to-r from-cyber-pink/20 to-cyber-purple/20 py-4 px-6 rounded-lg inline-block">
              <p className="text-cyber-neon font-bold">LIMITED TIME: 20% OFF ALL PACKAGES</p>
              <p className="text-white text-sm">Offer valid until July 31st. Use code EVENT20 when ordering.</p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-cyber-dark border-t border-cyber-purple/30 pt-8 pb-6">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="mb-6 md:mb-0">
              <h3 className="text-2xl font-orbitron font-bold text-white mb-2">CyberNest</h3>
              <p className="text-gray-400">Creating powerful websites for event management pros</p>
            </div>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-cyber-darker flex items-center justify-center text-white hover:bg-cyber-pink transition-colors">
                <FaInstagram />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-cyber-darker flex items-center justify-center text-white hover:bg-cyber-pink transition-colors">
                <FaFacebookF />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-cyber-darker flex items-center justify-center text-white hover:bg-cyber-pink transition-colors">
                <FaTwitter />
              </a>
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-400">© {new Date().getFullYear()} CyberNest Web Solutions. All rights reserved.</p>
            <p className="text-sm text-gray-500 mt-1">Creating next-generation websites for event management professionals.</p>
            <div className="mt-4">
              <a href="mailto:ankitmaharshi55@gmail.com" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Contact</a>
              <a href="#" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Demo Request Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="cyber-card p-6 max-w-lg w-full relative"
          >
            <button 
              onClick={() => setShowDemoModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            
            <h3 className="text-2xl font-orbitron font-bold mb-4 text-white">Ready to Level Up Your Event Business?</h3>
            <p className="text-gray-300 mb-6">Sign up now and get 20% off any website package. Limited time offer for event management professionals!</p>
            
            <form>
              <div className="mb-4">
                <label htmlFor="demoName" className="block text-white mb-2">Your Name <span className="text-cyber-pink">*</span></label>
                <input 
                  type="text" 
                  id="demoName"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="demoEmail" className="block text-white mb-2">Email Address <span className="text-cyber-pink">*</span></label>
                <input 
                  type="email" 
                  id="demoEmail"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="mb-6">
                <label htmlFor="demoPhone" className="block text-white mb-2">Phone Number <span className="text-cyber-pink">*</span></label>
                <input 
                  type="tel" 
                  id="demoPhone"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="flex justify-between">
                <button 
                  type="button"
                  onClick={() => setShowDemoModal(false)} 
                  className="cyber-button-outline"
                >
                  <span>Maybe Later</span>
                </button>
                <button type="submit" className="cyber-button">
                  <span>Get 20% Off</span>
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default EventManagementSiteLanding; 